/**
 * PageGate · v2.7.0 batch DI
 * ===========================
 * Lightweight wrapper that consults `/api/site/page-visibility` (cached in
 * sessionStorage) and either renders `children` or redirects to `/`. Used to
 * gate the top-level marketing routes from the admin Page Visibility toggles.
 *
 * Fallback rule (per spec): unknown / missing keys default to VISIBLE so a
 * newly-added route never gets hidden accidentally.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";

const CACHE_KEY = "bullertaxi.page_visibility.v1";
let inFlight = null;
let cached = null;

function loadCached() {
  if (cached) return cached;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) cached = JSON.parse(raw);
  } catch { /* sessionStorage unavailable in some browsers/incognito */ }
  return cached;
}

export function clearPageVisibilityCache() {
  cached = null;
  try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

function fetchVisibility() {
  if (inFlight) return inFlight;
  inFlight = api.get("/site/page-visibility")
    .then((r) => {
      cached = r.data || {};
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cached)); } catch { /* ignore */ }
      return cached;
    })
    .catch(() => {
      cached = {}; // fail-open: empty map → every key is visible per fallback
      return cached;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

export default function PageGate({ pageKey, children, redirectTo = "/" }) {
  const [vis, setVis] = useState(loadCached());

  useEffect(() => {
    let mounted = true;
    if (!vis) {
      fetchVisibility().then((v) => { if (mounted) setVis(v); });
    }
    return () => { mounted = false; };
  }, [vis]);

  // First-paint: while we don't yet have the map, render children optimistically
  // so visitors don't see a flash of redirect. The fetch is sub-100ms; if the
  // page IS disabled we'll redirect on the next render tick. Trade-off favors
  // perceived speed over a 50-ms loading spinner.
  if (vis && vis[pageKey] === false) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
}
